import { useState, useMemo } from 'react';
import DashboardLayout from '@/components/feature/DashboardLayout';
import ContextualTour from '@/components/feature/ContextualTour';
import { PageHeader } from '@/pages/dashboard/shared';
import { useGlobalTrainingData, useTrainingStats } from '@/pages/dashboard/shared-data';
import type { TreinamentoStatus } from '@/pages/dashboard/shared-data';
import RegistrarTreinamento from './RegistrarTreinamento';

const treinamentosTourSteps = [
  {
    title: 'Gestão de Treinamentos',
    description:
      'Visão consolidada de todos os treinamentos dos funcionários. Monitore vencimentos, status e conformidade em um só lugar.',
    icon: 'ri-award-line',
  },
  {
    title: 'Cards de resumo',
    description:
      'Os cards no topo mostram a saúde geral dos treinamentos: total, vigentes, próximos do vencimento (até 30 dias) e vencidos. Fique de olho nos números vermelhos!',
    icon: 'ri-dashboard-line',
  },
  {
    title: 'Filtros por status',
    description:
      'Use os botões de filtro para ver apenas treinamentos vigentes, a vencer ou vencidos. Isso ajuda a priorizar as ações de renovação.',
    icon: 'ri-filter-line',
  },
  {
    title: 'Tabela de vencimentos',
    description:
      'A tabela lista cada treinamento com funcionário, data de conclusão, vencimento e status. Treinamentos vencidos aparecem com fundo vermelho para chamar atenção imediata.',
    icon: 'ri-table-line',
  },
];

export default function TreinamentosPage() {
  const {
    all: allTreinamentos,
    loading: treinamentosLoading,
    recarregar,
  } = useGlobalTrainingData();
  const [registrando, setRegistrando] = useState(false);
  const trainingStats = useTrainingStats(allTreinamentos);
  const [filtroStatus, setFiltroStatus] = useState<'todos' | TreinamentoStatus>('todos');

  const treinamentosFiltrados = useMemo(() => {
    if (filtroStatus === 'todos') return allTreinamentos;
    return allTreinamentos.filter((t) => t.status === filtroStatus);
  }, [allTreinamentos, filtroStatus]);

  const statusCounts = useMemo(
    () => ({
      todos: allTreinamentos.length,
      vencido: allTreinamentos.filter((t) => t.status === 'vencido').length,
      a_vencer: allTreinamentos.filter((t) => t.status === 'a_vencer').length,
      vigente: allTreinamentos.filter((t) => t.status === 'vigente').length,
    }),
    [allTreinamentos],
  );

  const formatDate = (iso: string) => {
    const [y, m, d] = iso.split('-').map(Number);
    return `${d.toString().padStart(2, '0')}/${m.toString().padStart(2, '0')}/${y}`;
  };

  const filtroOptions: {
    key: 'todos' | TreinamentoStatus;
    label: string;
    count: number;
    activeClass: string;
  }[] = [
    {
      key: 'todos',
      label: 'Todos',
      count: statusCounts.todos,
      activeClass: 'bg-foreground-900 text-background-50',
    },
    {
      key: 'vigente',
      label: 'Vigentes',
      count: statusCounts.vigente,
      activeClass: 'bg-green-600 text-white',
    },
    {
      key: 'a_vencer',
      label: 'A Vencer',
      count: statusCounts.a_vencer,
      activeClass: 'bg-amber-500 text-white',
    },
    {
      key: 'vencido',
      label: 'Vencidos',
      count: statusCounts.vencido,
      activeClass: 'bg-red-500 text-white',
    },
  ];

  return (
    <DashboardLayout>
      <div className="max-w-6xl">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <PageHeader
            title="Treinamentos"
            subtitle="Controle de vigência de todos os treinamentos dos funcionários"
          />
          <button
            onClick={() => setRegistrando(true)}
            className="inline-flex items-center gap-2 px-4 py-2.5 rounded-md bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 transition-colors whitespace-nowrap shrink-0"
          >
            <i className="ri-add-line"></i>
            Registrar Treinamento
          </button>
        </div>

        {registrando && (
          <RegistrarTreinamento onFechar={() => setRegistrando(false)} onRegistrado={recarregar} />
        )}

        {/* Summary cards */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
          {[
            {
              label: 'Total',
              valor: trainingStats.total,
              cor: 'bg-background-50 border-background-200/70',
              texto: 'text-foreground-900',
            },
            {
              label: 'Vigentes',
              valor: trainingStats.vigentes,
              cor: 'bg-green-50 border-green-200',
              texto: 'text-green-700',
            },
            {
              label: 'A Vencer (≤30d)',
              valor: trainingStats.aVencer,
              cor: 'bg-amber-50 border-amber-200',
              texto: 'text-amber-700',
            },
            {
              label: 'Vencidos',
              valor: trainingStats.vencidos,
              cor: 'bg-red-50 border-red-200',
              texto: 'text-red-700',
            },
          ].map((card) => (
            <div key={card.label} className={`${card.cor} border rounded-lg p-3 md:p-4`}>
              <p className="text-xs font-medium text-foreground-500">{card.label}</p>
              <p className={`text-xl md:text-2xl font-bold mt-1 ${card.texto}`}>{card.valor}</p>
            </div>
          ))}
        </div>

        {/* Banner de alerta se houver vencidos */}
        {trainingStats.vencidos > 0 && (
          <div className="mb-6 p-4 rounded-lg border border-red-200 bg-red-50/60 flex items-start gap-3">
            <div className="w-8 h-8 rounded-full bg-red-100 flex items-center justify-center shrink-0 mt-0.5">
              <i className="ri-error-warning-line text-red-600 text-sm"></i>
            </div>
            <div>
              <p className="text-sm font-semibold text-red-800">
                Atenção! {trainingStats.vencidos} treinamento(s) vencido(s)
              </p>
              <p className="text-xs text-red-600 mt-0.5">
                {trainingStats.vencidos > 1
                  ? 'Existem treinamentos com vigência expirada que precisam ser renovados com urgência.'
                  : 'Existe um treinamento com vigência expirada que precisa ser renovado com urgência.'}
              </p>
            </div>
            <button
              onClick={() => setFiltroStatus('vencido')}
              className="ml-auto shrink-0 px-3 py-1.5 rounded-md bg-red-500 text-white text-xs font-medium hover:bg-red-600 transition-colors whitespace-nowrap"
            >
              Ver vencidos
            </button>
          </div>
        )}

        {/* Filtros */}
        <div className="flex flex-wrap items-center gap-2 mb-6">
          {filtroOptions.map((opt) => (
            <button
              key={opt.key}
              onClick={() => setFiltroStatus(opt.key)}
              className={`px-4 py-1.5 rounded-full text-xs font-medium border transition-colors whitespace-nowrap ${
                filtroStatus === opt.key
                  ? `${opt.activeClass} border-transparent`
                  : 'bg-background-50 border-background-300/60 text-foreground-600 hover:bg-background-100'
              }`}
            >
              {opt.label} <span className="ml-1 opacity-70">({opt.count})</span>
            </button>
          ))}
        </div>

        {/* Tabela de treinamentos */}
        <div className="bg-background-50 border border-background-200/70 rounded-lg overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-background-200/70 bg-background-50">
                  <th className="text-left px-5 py-3 text-xs font-semibold text-foreground-500 uppercase tracking-wider whitespace-nowrap">
                    Funcionário
                  </th>
                  <th className="text-left px-5 py-3 text-xs font-semibold text-foreground-500 uppercase tracking-wider whitespace-nowrap">
                    Função
                  </th>
                  <th className="text-left px-5 py-3 text-xs font-semibold text-foreground-500 uppercase tracking-wider whitespace-nowrap">
                    Treinamento
                  </th>
                  <th className="text-left px-5 py-3 text-xs font-semibold text-foreground-500 uppercase tracking-wider whitespace-nowrap">
                    Conclusão
                  </th>
                  <th className="text-left px-5 py-3 text-xs font-semibold text-foreground-500 uppercase tracking-wider whitespace-nowrap">
                    Vencimento
                  </th>
                  <th className="text-left px-5 py-3 text-xs font-semibold text-foreground-500 uppercase tracking-wider whitespace-nowrap">
                    Status
                  </th>
                  <th className="text-left px-5 py-3 text-xs font-semibold text-foreground-500 uppercase tracking-wider whitespace-nowrap">
                    Dias
                  </th>
                </tr>
              </thead>
              <tbody>
                {treinamentosFiltrados.length === 0 ? (
                  <tr>
                    <td colSpan={7} className="px-5 py-12 text-center text-sm text-foreground-500">
                      Nenhum treinamento encontrado com este filtro
                    </td>
                  </tr>
                ) : (
                  treinamentosFiltrados.map((treino, idx) => {
                    const isVencido = treino.status === 'vencido';
                    const isAVencer = treino.status === 'a_vencer';
                    return (
                      <tr
                        key={idx}
                        className={`border-b border-background-200/50 transition-colors ${
                          isVencido
                            ? 'bg-red-50/30 hover:bg-red-50/50'
                            : isAVencer
                              ? 'bg-amber-50/30 hover:bg-amber-50/50'
                              : 'hover:bg-background-50'
                        }`}
                      >
                        <td className="px-5 py-3.5 whitespace-nowrap">
                          <span className="font-medium text-foreground-800">
                            {treino.funcionario}
                          </span>
                        </td>
                        <td className="px-5 py-3.5 whitespace-nowrap">
                          <span className="text-foreground-600">{treino.funcao}</span>
                        </td>
                        <td className="px-5 py-3.5">
                          <div className="flex items-center gap-1.5 flex-wrap">
                            <span className="font-medium text-foreground-800">
                              {treino.treinamento}
                            </span>
                            {treino.obrigatorio && (
                              <span className="inline-block px-1.5 py-0.5 rounded-full text-[10px] font-medium border bg-red-50 text-red-700 border-red-200 whitespace-nowrap">
                                Obrig.
                              </span>
                            )}
                          </div>
                        </td>
                        <td className="px-5 py-3.5 whitespace-nowrap text-foreground-600">
                          {formatDate(treino.dataConclusao)}
                        </td>
                        <td className="px-5 py-3.5 whitespace-nowrap">
                          <span
                            className={`font-medium ${isVencido ? 'text-red-600' : isAVencer ? 'text-amber-600' : 'text-foreground-700'}`}
                          >
                            {formatDate(treino.dataVencimento)}
                          </span>
                        </td>
                        <td className="px-5 py-3.5 whitespace-nowrap">
                          <span
                            className={`inline-block px-2.5 py-0.5 rounded-full text-xs font-medium border ${
                              isVencido
                                ? 'bg-red-50 text-red-700 border-red-200'
                                : isAVencer
                                  ? 'bg-amber-50 text-amber-700 border-amber-200'
                                  : 'bg-green-50 text-green-700 border-green-200'
                            }`}
                          >
                            {treino.label}
                          </span>
                        </td>
                        <td className="px-5 py-3.5 whitespace-nowrap">
                          <span
                            className={`text-xs font-medium ${
                              isVencido
                                ? 'text-red-600'
                                : isAVencer
                                  ? 'text-amber-600'
                                  : 'text-green-600'
                            }`}
                          >
                            {isVencido
                              ? `-${treino.dias}d`
                              : isAVencer
                                ? `${treino.dias}d`
                                : `+${treino.dias}d`}
                          </span>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>

          {treinamentosFiltrados.length > 0 && (
            <div className="px-5 py-3 border-t border-background-200/70 bg-background-50">
              <p className="text-xs text-foreground-500">
                Exibindo {treinamentosFiltrados.length} de {allTreinamentos.length} treinamento(s)
              </p>
            </div>
          )}
        </div>
      </div>

      <ContextualTour
        storageKey="tour_treinamentos_gestao"
        steps={treinamentosTourSteps}
        title="Gestão de Treinamentos"
      />
    </DashboardLayout>
  );
}
